/**
 * 회의록 «보내기» 의 순수 함수 — 화면·서버와 떨어져 있다.
 *
 * ## ★★★ 여기 있는 것이 「누구에게 갔는지」를 정한다
 *
 * 보내기는 **되돌릴 수 없다**(사용자 결정). 그래서 이 파일의 판정이 틀리면 **거둘 수 없는 잘못**이
 * 된다 — 화면 모양보다 이쪽을 먼저 잠근다. 세 가지가 여기 있고, 셋 다 그 까닭이 실측에서 왔다.
 *
 * ```
 * matchAttendeeCandidates   이름 글자 → 조직도 사람. ⛔ 「기본 선택」을 만들지 않는다
 * formatRecipientTrail      이력에 적을 받는 사람. ⛔ 「3명」처럼 수만 남기지 않는다
 * summarizeRecipientPick    고른 것 요약. 「참석자 후보」와 「고른 수」를 갈라 적는다
 * ```
 */
import type { Picked, PickerGroup, PickerPerson } from '../../components/shared/EntityPicker/entity-picker-core';
import { peopleOf } from '../../components/shared/EntityPicker/entity-picker-core';

/** 보낼 곳 — 셋뿐이다. ⛔ 늘리려면 서버 배관도 같이 늘어난다. */
export const MINUTES_SEND_CHANNELS = ['approval', 'message', 'mail'] as const;
export type MinutesSendChannel = (typeof MINUTES_SEND_CHANNELS)[number];

/**
 * 이름 글자 하나가 조직도에서 누구인지.
 *
 * ## ⛔⛔ 「정확히 한 명」이 아니면 후보도 아니다
 *
 * 회의록의 `attendees` 는 **이름 글자 배열**이다(`meeting_minutes.attendees text[]`) — 사람(user_id)이
 * 아니다. 그래서 이름으로 조직도를 되짚어야 하는데, 거기 함정이 둘이다:
 *
 *   ① **전사가 이름을 틀린다** — 실측에서 「이수연」이 「이수영」으로 적혔다. 그 글자는 조직도의
 *      다른 사람(있다면 그 사람)과 **맞아 버린다.**
 *   ② **동명이인** — 같은 이름이 둘이면 «어느 쪽인지 알 길이 없다». 녹음을 안 보내니(사용자 결정)
 *      받는 사람은 **글만 본다** — 틀린 것을 되짚을 길이 아예 없다.
 *
 * 그래서 이 함수는 **고르지 않는다.** 정확히 한 명과 맞을 때만 「후보」로 올리고, 둘 이상이면
 * `ambiguous` 로 밀어 **배지조차 달지 않는다**. 켜는 것은 언제나 사람이 한다.
 */
export interface AttendeeMatch {
  /** 정확히 한 명과 맞은 것 — 「참석자 후보」 배지를 달아 위로 올린다. ⛔ 체크는 켜지 않는다. */
  candidates: PickerPerson[];
  /** 같은 이름이 둘 이상 — 어느 쪽인지 모르므로 후보로도 안 쓴다. */
  ambiguous: string[];
  /** 조직도에 없는 이름 — 전사 오류이거나 외부 참석자다. */
  unmatched: string[];
}

/** 이름 비교는 «공백을 지우고 소문자로». 「김 민수」와 「김민수」는 같은 사람이다. */
function normName(s: string): string {
  return s.replace(/\s+/g, '').toLowerCase();
}

/** 트리를 훑어 사람을 한 줄로 펼친다(같은 사람이 여러 팀에 있으면 id 로 한 번만). */
export function flattenPeople(groups: ReadonlyArray<PickerGroup>, loose?: ReadonlyArray<PickerPerson>): PickerPerson[] {
  const out: PickerPerson[] = [];
  const seen = new Set<string>();
  const push = (p: PickerPerson): void => {
    if (seen.has(p.id)) return;
    seen.add(p.id);
    out.push(p);
  };
  const walk = (g: PickerGroup): void => {
    for (const p of peopleOf(g)) push(p);
    for (const c of g.children ?? []) walk(c);
  };
  for (const g of groups) walk(g);
  for (const p of loose ?? []) push(p);
  return out;
}

export function matchAttendeeCandidates(
  attendeeNames: ReadonlyArray<string>,
  people: ReadonlyArray<PickerPerson>,
): AttendeeMatch {
  const byName = new Map<string, PickerPerson[]>();
  for (const p of people) {
    const k = normName(p.name);
    const bucket = byName.get(k);
    if (bucket) bucket.push(p);
    else byName.set(k, [p]);
  }

  const candidates: PickerPerson[] = [];
  const ambiguous: string[] = [];
  const unmatched: string[] = [];
  const takenIds = new Set<string>();
  const seenNames = new Set<string>();

  for (const raw of attendeeNames) {
    const name = raw.trim();
    if (name === '') continue;
    const k = normName(name);
    // 같은 이름이 명단에 두 번 적혀 있어도 후보는 한 번만 올린다.
    if (seenNames.has(k)) continue;
    seenNames.add(k);

    const hit = byName.get(k);
    if (!hit || hit.length === 0) {
      unmatched.push(name);
      continue;
    }
    if (hit.length > 1) {
      // ⛔ 동명이인 — 여기서 하나를 고르면 «잘못 보낸다». 후보로도 쓰지 않는다.
      ambiguous.push(name);
      continue;
    }
    const only = hit[0]!;
    if (!takenIds.has(only.id)) {
      takenIds.add(only.id);
      candidates.push(only);
    }
  }
  return { candidates, ambiguous, unmatched };
}

/**
 * 이력에 적을 받는 사람 글자.
 *
 * ## ⛔ 「3명」처럼 수만 남기면 되짚을 수 없다
 *
 * 되돌리기가 없으므로 이력이 **유일한 되짚을 길**이다(사용자 결정). 그래서 접어도 **앞 몇 명은
 * 반드시 이름이 남는다** — 「외 28명」만 남는 상태를 만들지 않는다.
 */
export function formatRecipientTrail(names: ReadonlyArray<string>, head = 2): string {
  const kept = names.filter((n) => n.trim() !== '');
  if (kept.length === 0) return '';
  // head 가 0·음수로 와도 «수만 남는» 상태를 만들지 않는다 — 최소 한 명은 적는다.
  const take = Math.max(1, Math.trunc(head));
  if (kept.length <= take) return kept.join(' · ');
  const rest = kept.length - take;
  return `${kept.slice(0, take).join(' · ')} 외 ${rest}명`;
}

/** 고른 것 요약 — 「고른 수」와 「참석자 후보」는 뜻이 달라 갈라 적는다. */
export interface RecipientPickSummary {
  /** 실제로 보낼 사람 수 — 팀째 고른 것은 사람으로 펼친 수다. */
  personCount: number;
  /** 아직 켜지 않은 참석자 후보 수. ⛔ 자동으로 켜지 않는다는 사실을 화면이 말해야 한다. */
  candidateCount: number;
}

export function summarizeRecipientPick(
  picked: ReadonlyArray<Picked>,
  groups: ReadonlyArray<PickerGroup>,
  candidates: ReadonlyArray<PickerPerson>,
): RecipientPickSummary {
  const ids = new Set<string>();
  for (const p of picked) {
    if (p.kind === 'person') {
      ids.add(p.id);
      continue;
    }
    // 팀·부서째 고른 것 — 그 아래 사람을 펼쳐 센다(고른 묶음이 겹쳐도 한 번만).
    const found = findGroup(groups, p.id);
    if (found) for (const person of flattenPeople([found])) ids.add(person.id);
  }
  // 이미 켜진 후보는 「아직 안 켠 후보」에서 뺀다.
  const pendingCandidates = candidates.filter((c) => !ids.has(c.id)).length;
  return { personCount: ids.size, candidateCount: pendingCandidates };
}

/* ══════════════ 보내는 «글자» ══════════════ */

/**
 * 메시지 본문 상한. 서버 `messages.routes.ts` 의 10,000 과 **같은 수**여야 한다.
 * ⛔ 여기서 안 자르면 서버가 400 을 주고, 그 400 은 「보내기 실패」로 보인다.
 */
export const MAX_MESSAGE_BODY = 10_000;

/**
 * 메일 본문 상한 — **`null`. 자르지 않는다.**
 *
 * ## ⛔⛔ 「1,000,000자」는 틀린 근거였다 (2026-09-22 정정)
 *
 * 한때 `1_000_000` 을 넣었다. 그 수는 `mail.routes.ts` 의 `composeBodySchema` 에 **적혀 있는 글자**
 * 였고 **실측이 아니었다.** 실제로는 그 수에 **닿을 수 없다**:
 *
 * ```
 * chat-server 는 보내기·메일 라우트에 bodyLimit 을 «안 걸었다»  → Fastify 기본 «1MB»
 *                (내가 origin/main 을 훑어 확인: 다른 라우트엔 bodyLimit 이 있고 이쪽엔 없다)
 * 한글은 UTF-8 로 한 자 «3바이트»                              → 실제 한도 «약 349,000자»
 * 넘으면 «우리 핸들러가 돌기 전에» 413                          → code FST_ERR_CTP_BODY_TOO_LARGE
 * ```
 *
 * ## ★ 결론은 그대로 — 「메일은 안 자른다」. 까닭이 바뀐다
 *
 * 「상한이 커서 안 자른다」가 아니라 **「자를 «자리»가 애초에 없다」**다:
 *   · 실제 한도는 **바이트**이고 이 함수가 세는 것은 **글자**다 — 단위가 다르다.
 *   · 그 1MB 는 **요청 전체**다(제목·user_ids·emails·JSON 따옴표까지). 본문만 재서 자르면
 *     **틀린 경계**로 자르게 된다 — 멀쩡한 글을 우리가 잘라 놓고 「길어서 줄였습니다」라고 적는다.
 * ⇒ 그래서 자르지 않고, **413 을 받아 「본문이 너무 깁니다」라고 말한다**
 *   (`api/minutes-send.ts` 의 `body-too-large` 갈래). ⛔ 그 말은 **그 갈래에만** 붙인다 —
 *   다른 400 과 같은 문구를 쓰면 사용자가 받는 사람·주소를 고치려 든다.
 */
export const MAX_MAIL_BODY: number | null = null;

/** 갈래마다 다른 상한 — 한곳에서 고른다(화면이 저마다 고르면 어느 날 하나만 낡는다). */
export function bodyLimitFor(channel: MinutesSendChannel | null): number | null {
  // ⚠ 메일은 `null` — 자르지 않는다(위 `MAX_MAIL_BODY` 의 까닭). 메시지만 실제로 자른다.
  return channel === 'mail' ? MAX_MAIL_BODY : MAX_MESSAGE_BODY;
}

/** 글자를 만드는 데 필요한 것만 — `MinutesDetail` 전부를 받지 않는다(원문이 섞여 들어올 여지를 없앤다). */
export interface SendTextSource {
  title: string;
  summary: string | null;
  decisions: readonly string[];
  action_items: readonly { text: string; assignee?: string; due?: string }[];
}

export interface SendText {
  subject: string;
  body: string;
  /** 잘렸나 — 화면이 **보내기 전에** 그 사실을 적기 위해 필요하다. */
  truncated: boolean;
  /** 자르기 «전» 길이. 미리보기가 「10,240자 → 10,000자로 줄여 보냅니다」를 적는 데 쓴다. */
  fullLength: number;
}

/** 잘린 자리에 남기는 한 줄. 시험이 이 글자를 본다 — 바꾸면 시험도 함께 바꾼다. */
export const CUT_NOTE = '\n\n(길어서 줄여 보냈습니다)';

/**
 * 보낼 글자를 만든다.
 *
 * ## ⛔ 전사 «원문»은 넣지 않는다
 *
 * 「회의록 본문만」이 결정인데, `source_text` 는 회의록이 아니라 **재료**다 — 잡담과 오인식이 그대로
 * 들어 있다. 요약·결정·액션이 사람이 「회의록」이라 부르는 것이다(상세 화면의 「양식정리」가 그것이다).
 *
 * ## ⛔⛔ 「회의록 열기」 링크를 «넣지 않는다» (2026-09-22 사용자 결정)
 *
 * 처음 판에는 링크를 넣었다. 빠졌다 — **회의록 본문은 «작성자만» 본다**(9/21 결정)가 그대로
 * 유지되기 때문이다. 링크를 넣으면 받는 사람이 눌렀을 때 **아무것도 못 보는 주소**를 준 셈이 되고,
 * 그것을 되려 열어 주려면 열람 선을 넓혀야 한다 — 사용자가 넓히지 않는 쪽을 택했다.
 * ⇒ 세 갈래가 다 같은 모양이다: **내용을 «복사해» 보내고, 회의록 자체의 열람 선은 건드리지 않는다.**
 *
 * ## ★★ 화면이 만들어 «실어 보낸다» — 서버가 따로 만들지 않는다
 *
 * 서버가 같은 글자를 다시 만들면 **두 벌**이 되고, 그러면 **미리보기로 본 것과 실제로 간 것이
 * 달라질 수 있다.** 되돌리기가 없는 기능에서 그 어긋남은 되짚을 수 없다. 그래서 미리보기에 그린
 * 바로 그 글자를 요청에 싣는다.
 *
 * ## ⚠ 자를 때는 «자랐다고 말한다» — 이제 도피처가 없다
 *
 * 링크가 없어져 **잘린 뒷부분을 볼 길이 아예 없다.** 그래서 ① 잘린 자리에 한 줄을 남기고
 * ② 보내기 «전에» 화면이 「줄여 보냅니다」를 적는다(`truncated`·`fullLength` 가 그것을 위해 있다).
 * ⛔ 서버가 잘라 주기를 믿지 않는다 — 넘기면 ajv 가 **400** 을 주고, 그 400 은 사용자에게
 *   «아무 일도 안 일어난 것»으로 보인다.
 *
 * @param limit 상한. `null` 이면 자르지 않는다(메일 — 상한을 아직 «안 쟀다»).
 */
export function buildSendText(src: SendTextSource, limit: number | null = MAX_MESSAGE_BODY): SendText {
  const lines: string[] = [];
  if (src.summary !== null && src.summary.trim() !== '') lines.push(src.summary.trim());
  if (src.decisions.length > 0) {
    lines.push('', '[결정사항]');
    src.decisions.forEach((d, i) => lines.push(`${i + 1}. ${d}`));
  }
  if (src.action_items.length > 0) {
    lines.push('', '[액션아이템]');
    for (const a of src.action_items) {
      const meta = [a.assignee, a.due].filter((v) => v !== undefined && v !== '').join(', ');
      lines.push(`- ${a.text}${meta !== '' ? ` (${meta})` : ''}`);
    }
  }
  const head = lines.join('\n').trim();
  const fullLength = head.length;

  if (limit === null || fullLength <= limit) {
    return { subject: src.title, body: head, truncated: false, fullLength };
  }
  // 줄임 한 줄도 «상한 안에» 들어가야 한다 — 그것 때문에 상한을 넘기면 서버가 400 을 준다.
  const room = Math.max(0, limit - CUT_NOTE.length);
  return {
    subject: src.title,
    body: `${head.slice(0, room).trimEnd()}${CUT_NOTE}`,
    truncated: true,
    fullLength,
  };
}

function findGroup(groups: ReadonlyArray<PickerGroup>, id: string): PickerGroup | null {
  for (const g of groups) {
    if (g.id === id) return g;
    const hit = findGroup(g.children ?? [], id);
    if (hit) return hit;
  }
  return null;
}

/* ────────────────────────────────────────────────────────────────────────────
 * 보낸 «뒤» — 무엇을 말해도 되나 (2026-09-22 운영 결함에서 나온 자리)
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * ★★★ **「보냈습니다」는 말해야 한다.**
 *
 * ## ⛔ 운영에서 난 결함 — 「전달됨」을 피하려다 「보냈습니다」까지 지웠다
 *
 * 사용자 말 그대로: 「**보내기를 누르고 나면 '발송되었습니다.' 라는 문구가 하나도 안 나온다**」.
 * 화면은 `logged === false` 와 `truncated` «둘만» 말하고 있었다 — 정상 경로에서는 **아무 말도
 * 안 했다.** 「전달됨」을 피한 것은 옳았고, 한 걸음 지나쳤을 뿐이다.
 *
 * ## ★ 선은 「서버가 아는가」다 — 「보냈다」와 「닿았다」 사이
 *
 * ```
 * ⛔ 말하면 안 되는 것   「전달되었습니다」·「도착」·「읽었습니다」  → 서버가 «모르는» 사실
 * ★  말해도 되는 것      「N명에게 보냈습니다」                     → 201 이 이미 말해 준 사실
 * ```
 * 이력에 `delivered`·`status` 칸을 두지 않은 것과 **같은 결**이다. 「보냈다」는 우리가 한 일이고
 * 「닿았다」는 우리가 **못 보는** 일이다.
 *
 * ## ⛔ 모르는 수를 «만들지 않는다»
 *
 * 서버가 `recipients` 를 안 주면(옛 배포·비정형 응답) **수 없이** 말한다. 「1명에게 보냈습니다」로
 * 넘겨짚으면 셋에게 보낸 사람이 **하나만 갔다고 읽는다** — 되돌릴 수 없는 기능에서 그것은 거짓이다.
 */
export interface SentAckFacts {
  channel: 'message' | 'mail';
  recipients?: number;
  truncated?: boolean;
  logged?: boolean;
  mail_id?: string | null;
}

/** 방금 보낸 것에 대해 **말해도 되는 것만** 담은 것. */
export interface SentSay {
  channel: 'message' | 'mail';
  /** 받는 사람 «수» — ⛔ 모르면 `null` 이고, 그때 화면은 수를 «안 적는다». */
  count: number | null;
  /** ⚠ 이력에 «안 남았다» — 서버가 `false` 라고 «말했을 때만» 참이다(모르면 잠자코 있는다). */
  notLogged: boolean;
  /** 본문이 잘려서 갔다. */
  truncated: boolean;
  /** 메일 문서 번호 — 서버가 준 손잡이다. ⛔ 「도착」이 아니라 «만들어진 문서»를 가리킨다. */
  mailId: string | null;
}

/**
 * 201 응답 → 화면이 **말해도 되는 것**.
 *
 * ⛔ `MinutesSendAck` 을 `import` 하지 «않는다» — `api/minutes-send` 가 이 파일에서 타입을 가져가므로
 *   서로 부르는 꼴이 된다. 구조가 같으면 그대로 넘어오니(구조적 타이핑) 그 편이 안전하다.
 */
export function describeSentAck(ack: SentAckFacts): SentSay {
  const n = ack.recipients;
  return {
    channel: ack.channel,
    // ⛔ 0 도 «수 없음»으로 다룬다 — 「0명에게 보냈습니다」는 말이 안 되고, 서버는 빈 명단을 거절한다.
    count: typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : null,
    notLogged: ack.logged === false,
    truncated: ack.truncated === true,
    mailId: ack.channel === 'mail' && typeof ack.mail_id === 'string' && ack.mail_id !== ''
      ? ack.mail_id
      : null,
  };
}

/**
 * ★★★ **또 누르는 것을 막는 근거** — 「이미 보냈다」는 **이력이 안다**.
 *
 * ## ⛔ 단추 잠금은 한 번의 클릭 «안»에서만 막는다
 *
 * 보내는 동안 단추를 잠그는 것은 **연타**만 막는다. 패널을 닫았다 다시 열면 그 잠금은 풀려 있고,
 * 사람은 「눌렀는지 확신이 없으면 한 번 더 누른다」 — 그러면 **방 둘 · 메일 두 통 · 이력 두 줄**이고
 * 되돌릴 수 없다.
 *
 * ## ★ 그래서 «영구히 잠그지» 않고 «사실을 보여 준다»
 *
 * 같은 회의록을 두 번 보내는 것이 **정당한 경우가 있다**(다른 사람에게, 다른 갈래로). 그러니
 * 막는 것이 아니라 **이미 보낸 사실을 보내기 화면 «안»에서 먼저 읽히게** 한다.
 * ⛔ 「정말 보내시겠습니까」·「되돌릴 수 없습니다」류의 «되묻는 말»은 넣지 않는다(사용자 결정).
 *   적는 것은 **사실 한 줄**이다 — 몇 번, 마지막이 언제·어느 갈래.
 */
export function summarizePriorSends<T extends { sent_at: string }>(
  items: readonly T[],
): { count: number; last: T | null } {
  let last: T | null = null;
  for (const it of items) {
    // ★ 글자 그대로 비교한다 — ISO 시각은 사전순이 곧 시간순이라 파싱 실패로 NaN 이 될 일이 없다.
    if (last === null || it.sent_at > last.sent_at) last = it;
  }
  return { count: items.length, last };
}
